import type { Axios } from "axios"
import axios from "axios"
import DataHelper from "@/helper/data-helper"
import {
  markHttpRequestForAppInsights,
  recordAppInsightsHttpResponse
} from "@/services/application-insights-service"
import { AuthController } from "@/controllers/auth-controller"
import { useAuthStore } from "@/store/auth.store"
import { startInactiveSpan, captureException } from "@sentry/vue"
import { Capacitor } from "@capacitor/core"

export default class AxiosWrapperService {
  private static _instance: AxiosWrapperService

  public static getInstance() {
    if (!this._instance) {
      this._instance = new AxiosWrapperService()
    }
    return this._instance
  }

  private readonly _axiosAdvanced: Axios

  private readonly _axiosBasic: Axios

  constructor() {
    // HTTPRequestBuilder is the sole owner of the API base URL. It passes
    // absolute production URLs and origin-relative preview URLs to Axios.
    // Applying VITE_API_URL here too would turn a preview request from
    // "/d/auth/..." into "/d/d/auth/...".
    this._axiosBasic = axios.create()
    this._axiosAdvanced = axios.create()

    // Add a request interceptor
    this._axiosAdvanced.interceptors.request.use(
      async (config) => {
        // Utilize controller static method because we cannot use inject mechanism here.
        const token = await AuthController.getAccessToken()
        if (token && config.headers) {
          config.headers.Authorization = token
        }

        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    this._axiosAdvanced.interceptors.request.use((originalRequest) => {
      originalRequest.data = DataHelper.convertMomentToDateStrings(originalRequest.data)
      originalRequest.__appInsightsTenant =
        useAuthStore().activeUser?.activeTenant?.name?.trim() || "unknown"
      markHttpRequestForAppInsights(originalRequest)
      return originalRequest
    })

    this._axiosAdvanced.interceptors.response.use(
      (originalResponse) => {
        recordAppInsightsHttpResponse(originalResponse.config, originalResponse.status, {
          headers: originalResponse.headers
        })
        if (originalResponse.config.responseType !== "blob") {
          originalResponse.data = DataHelper.convertDateStringsToMoment(originalResponse.data)
        }
        return originalResponse
      },
      (error) => {
        if (error.config) {
          recordAppInsightsHttpResponse(error.config, error.response?.status ?? 0, {
            headers: error.response?.headers
          })
        }
        if (error.response) {
          error.response = DataHelper.convertDateStringsToMoment(error.response)
        }
        return Promise.reject(error)
      }
    )

    this._axiosAdvanced.interceptors.response.use(undefined, async (error) => {
      if (error.response?.status === 401 && !error.config._retried) {
        error.config._retried = true
        error.config.headers.Authorization = await AuthController.getAccessToken()
        return this._axiosAdvanced.request(error.config)
      }
      return Promise.reject(error)
    })

    this._axiosAdvanced.interceptors.response.use(undefined, async (error) => {
      const config = error.config
      if (!config || config._transientRetried) {
        return Promise.reject(error)
      }

      const status = error.response?.status
      const isNetworkOrCorsError = !error.response && error.code !== "ERR_CANCELED"
      const isGatewayError = status === 502 || status === 503 || status === 504
      const isReadOnly = !config.method || config.method.toUpperCase() === "GET"

      if (isNetworkOrCorsError || (isGatewayError && isReadOnly)) {
        config._transientRetried = true
        await new Promise((resolve) => setTimeout(resolve, 500))
        return this._axiosAdvanced.request(config)
      }

      return Promise.reject(error)
    })

    // Capture HTTP requests and responses in Sentry (Sentry does not capture CapacitorHttp requests)
    // See PNTS-2052
    this._axiosAdvanced.interceptors.request.use((config) => {
      const span = startInactiveSpan({
        op: "http.client",
        name: `${(config.method || "").toUpperCase()} ${config.url}`
      })

      // record request details
      span.setAttribute("http.request.method", (config.method || "").toUpperCase())
      if (config.data) {
        span.setAttribute("http.request.body", JSON.stringify(config.data))
      }
      span.setAttribute("http.url", config.url ?? "")

      // stash for the response interceptor
      ;(config as any).__sentrySpan = span
      return config
    })

    this._axiosAdvanced.interceptors.response.use(
      (response) => {
        const span = (response.config as any).__sentrySpan
        if (span) {
          // record the status code as an attribute
          span.setAttribute("http.response.status_code", response.status)
          if (response.data) {
            span.setAttribute("http.response.body", JSON.stringify(response.data))
          }
          // optionally mark the span status
          span.setStatus("ok")
          // end() the span
          span.end()
        }
        return response
      },
      (error) => {
        const cfg = error.config || {}
        const span = (cfg as any).__sentrySpan
        if (span) {
          span.setAttribute("http.response.status_code", error.response?.status ?? 0)
          span.setStatus("internal_error")
          span.end()
        }
        captureException(error)
        // Log all errors to the console only in Capacitor environment
        if (Capacitor.isNativePlatform()) {
          const reqUrl = error?.config?.url
          const reqBody = error?.config?.data
          const statusCode = error?.response?.status
          const method = error?.config?.method
          const responseData = error?.response?.data
          console.log(
            "Request URL:",
            reqUrl,
            "Request Body:",
            reqBody,
            "Status Code:",
            statusCode,
            "Method:",
            method,
            "Response:",
            responseData
          )
        }
        return Promise.reject(error)
      }
    )
  }

  public getAxios() {
    return this._axiosAdvanced
  }
}
